import { useCallback, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser"; // Import from correct package
import fetchWithAuth from '@utils/fetch-with-auth';
import monitoringService from '@services/monitoring';
/**
 * Custom error class for API related errors originating from useApi or fetchWithAuth.
 * @extends Error
 */
class ApiError extends Error {
  /**
   * Creates an instance of ApiError.
   * @param {string} message - The error message.
   * @param {string} type - A specific error type code (e.g., 'network_error', 'auth_failed').
   * @param {number} status - The HTTP status code associated with the error (or 0 for network errors).
   * @param {Error|null} [originalError=null] - The original error object, if available.
   */
  constructor(message, type, status, originalError = null) {
    super(message);
    this.name = 'ApiError';
    this.type = type; // e.g., 'network_error', 'auth_failed', 'token_acquisition_failed', 'api_error', 'msal_in_progress'
    this.status = status; // HTTP status code or 0
    this.originalError = originalError; // Store original error for debugging
  }
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

/**
 * Custom hook to provide a function for making authenticated API calls.
 * Handles MSAL state checks (authentication, interaction status),
 * wraps fetchWithAuth, manages duplicate requests, and performs error mapping/retries.
 *
 * @returns {function(string, object, number): Promise<any>} A memoized function `callApi` to execute authenticated API requests.
 */
export const useApi = () => {
  const { instance, accounts, inProgress } = useMsal();
  const requestStateRef = useRef({
    pendingRequests: new Set(),
  }, []);

  /**
   * Makes an authenticated API call using fetchWithAuth.
   * Checks MSAL status, authentication, and prevents duplicate requests.
   * Handles specific errors like token acquisition failures with retries.
   * Maps errors to ApiError instances.
   *
   * @param {string} url - The full URL for the API request.
   * @param {object} [options={}] - Fetch options (method, body, headers, etc.).
   * @param {number} [retryCount=0] - Internal counter for retry attempts (primarily for token acquisition).
   * @returns {Promise<any>} A promise resolving with the API response data.
   * @throws {ApiError} Throws an ApiError for various failure conditions.
   */
  const callApi = useCallback(async (url, options = {}, retryCount = 0) => {
    const requestId = `${url}-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    const startTime = Date.now();

    monitoringService.trackEvent('ApiCallStarted', {
      requestId,
      url,
      method: options.method || 'GET',
      retryCount
    });

    try {
      // Check if MSAL is busy
      if (inProgress !== InteractionStatus.None) {
        monitoringService.trackEvent('ApiCallDelayed', {
          requestId,
          reason: 'msal_in_progress',
          status: inProgress
        });
        // Optionally wait or throw specific error
        // For now, let's throw an error to prevent calls while MSAL is busy
        throw new ApiError(
          `MSAL interaction is currently in progress (${inProgress}). Please try again shortly.`,
          'msal_in_progress',
          429 // Too Many Requests (or a custom status)
        );
      }

      // Check if authenticated
      if (!accounts || accounts.length === 0) {
         monitoringService.trackEvent('ApiAuthenticationRequired', {
           requestId,
           url
         });
         throw new ApiError(
           'User is not authenticated',
           'not_authenticated',
           401
         );
      }

      // Check if this request is already pending
      if (requestStateRef.current.pendingRequests.has(requestId)) {
        monitoringService.trackEvent('ApiCallDuplicate', {
          requestId,
          url,
          method: options.method || 'GET'
        });
        throw new ApiError(
          'Request already in progress',
          'duplicate_request',
          409
        );
      }

      requestStateRef.current.pendingRequests.add(requestId);
      
      // Pass instance and accounts to fetchWithAuth
      const response = await fetchWithAuth(url, options, instance, accounts);
      
      monitoringService.trackEvent('ApiCallSucceeded', {
        requestId,
        url,
        method: options.method || 'GET',
        duration: Date.now() - startTime,
        retryCount
      });
      
      return response;
    } catch (error) {
      const errorDetails = {
        requestId,
        url,
        method: options.method || 'GET',
        duration: Date.now() - startTime,
        retryCount,
        errorType: error.type || error.name,
        errorMessage: error.message
      };

      // Handle MSAL in progress error
      if (error.type === 'msal_in_progress') { throw error; }

      // Handle auth failures without recursive retries
      if (error.type === 'auth_failed') {
        monitoringService.trackEvent('ApiAuthFailure', {
          ...errorDetails
        });
        throw new ApiError(
          'Authentication failed',
          'auth_failed',
          401,
          error
        );
      }

      // Handle token acquisition failures
      if (error.type === 'token_acquisition_failed') {
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          monitoringService.trackEvent('ApiRetryingAfterTokenFailure', {
            ...errorDetails,
            nextRetryDelay: RETRY_DELAY * Math.pow(2, retryCount)
          });
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
          return callApi(url, options, retryCount + 1);
        }
        monitoringService.trackEvent('ApiTokenAcquisitionFailed', {
          ...errorDetails,
          attempts: MAX_RETRY_ATTEMPTS
        });
        throw new ApiError(
          'Failed to acquire authentication token after multiple attempts',
          'token_acquisition_failed',
          401,
          error
        );
      }

      // Enhance error with additional context
      if (error.name === 'FetchError') {
        monitoringService.trackException(error, {
          ...errorDetails,
          category: 'fetch'
        });
        throw new ApiError(
          error.message,
          error.type,
          error.status,
          error
        );
      }
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        monitoringService.trackException(error, {
          ...errorDetails,
          category: 'network'
        });
        throw new ApiError(
          'Network error occurred while making the API request',
          'network_error',
          0,
          error
        );
      }
      
      // Handle unexpected errors
      monitoringService.trackException(error, {
        ...errorDetails,
        category: 'unexpected'
      });
      throw new ApiError(
        'An unexpected error occurred while making the API request',
        'unexpected_error',
        500,
        error
      );
    } finally {
      requestStateRef.current.pendingRequests.delete(requestId);
    }
  }, [instance, accounts, inProgress]);

  return callApi;
};
